import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Ref } from 'vue';
import type { SubmissionContext } from 'vee-validate';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { useColorMode } from '@vueuse/core';
import { ResponseError } from '../../@generated/api-client';
import { createTextToast } from '@/utils/ToastUtils.ts';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const focusRefElement = (ref: Ref) => {
    if (ref.value) {
        ref.value['$el'].focus();
    }
};

export const setIntervalImmediately = (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    func: Function,
    interval: number,
): ReturnType<typeof setInterval> => {
    func();
    return window.setInterval(func, interval) as unknown as ReturnType<typeof setInterval>;
};

export const adjustElementHeight = (e: KeyboardEvent) => {
    const el = e.target as HTMLElement;
    if (!el) return;
    el.style.height = el.scrollHeight > el.clientHeight ? el.scrollHeight + 'px' : '10px';
};

export const handleFormError = async (
    form: SubmissionContext,
    error: ResponseError | unknown,
    pushToWebView: boolean,
    unknownErrorMessage = `An unknown error occurred. Please try again.`,
): Promise<void> => {
    if (!(error instanceof ResponseError)) {
        createTextToast(unknownErrorMessage, undefined, 'destructive', pushToWebView);

        return;
    }

    const errorData = await error.response.json();

    if (errorData.message === undefined) {
        createTextToast(unknownErrorMessage, undefined, 'destructive', pushToWebView);

        return;
    }

    let errorMessage = errorData.message;
    if (Array.isArray(errorMessage)) {
        errorMessage = errorMessage[0];
    }

    form.setErrors({
        _: errorMessage,
    });
};

export const handleApiError = async (
    error: ResponseError | unknown,
    unknownErrorMessage = `An unknown error occurred. Please try again.`,
    pushToWebView: boolean,
): Promise<void> => {
    if (!(error instanceof ResponseError)) {
        createTextToast(unknownErrorMessage, undefined, 'destructive', pushToWebView);

        return;
    }

    const errorData = await error.response.json();

    if (errorData.message === undefined) {
        createTextToast(unknownErrorMessage, undefined, 'destructive', pushToWebView);

        return;
    }

    let errorMessage = errorData.message;
    if (Array.isArray(errorMessage)) {
        errorMessage = errorMessage[0];
    }

    createTextToast(errorMessage, undefined, 'destructive', pushToWebView);
};

export const truncateString = (input: string, length: number) => {
    return input.length > length ? input.slice(0, length - 1).trim() + '...' : input;
};

export const setDarkModeClass = () => {
    const settingsStore = useSettingsStore();

    const mode = useColorMode();
    if (settingsStore.darkModeEnabled) {
        mode.value = 'dark';
    } else {
        mode.value = 'light';
    }
};

export const removeByIndexFilter = <T>(arr: T[], index: number): T[] => {
    return arr.filter((_, i) => i !== index);
};

export const moveArrayItem = <T>(arr: T[], i: number, d: 'up' | 'down'): T[] => {
    if (i < 0 || i >= arr.length) return arr;
    if (d === 'up' && i === 0) return arr;
    if (d === 'down' && i === arr.length - 1) return arr;

    const n = d === 'up' ? i - 1 : i + 1;
    const a = [...arr];
    const [x] = a.splice(i, 1);

    a.splice(n, 0, x);

    return a;
};

export const containsHtml = (input: string): boolean => {
    const regex = /<\/?[a-z][\s\S]*>/i;
    return regex.test(input);
};

export const slugify = (str: string, useUnderscore: boolean = false): string => {
    const replacement = useUnderscore ? '_' : '-';

    return str
        .toLowerCase() // Convert the string to lowercase.
        .normalize('NFD') // Normalize the string to separate diacritics.
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks.
        .replace(/[^a-z0-9]+/g, replacement) // Replace non-alphanumeric characters with the replacement.
        .replace(new RegExp(`^${replacement}+|${replacement}+$`, 'g'), ''); // Trim replacement characters from the beginning and end.
};

export const titleCase = (str: string): string => {
    return str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
        .join(' ');
};

export const getOs = (): string | null => {
    const userAgent = window.navigator.userAgent.toLowerCase(),
        macosPlatforms = /(macintosh|macintel|macppc|mac68k|macos)/i,
        windowsPlatforms = /(win32|win64|windows|wince)/i,
        iosPlatforms = /(iphone|ipad|ipod)/i;

    let os = null;

    if (macosPlatforms.test(userAgent)) {
        os = 'macos';
    } else if (iosPlatforms.test(userAgent)) {
        os = 'ios';
    } else if (windowsPlatforms.test(userAgent)) {
        os = 'windows';
    } else if (/android/.test(userAgent)) {
        os = 'android';
    } else if (/linux/.test(userAgent)) {
        os = 'linux';
    }

    return os;
};
